// Per-day-of-week appointment-type restrictions
// (AppointmentSettings.allowedTypesByDay). A day missing from the map
// allows every type; a day with a non-empty array allows only those
// types for customer self-bookings + AI bookings (admin bypasses).

export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const TYPE_LABELS: Record<string, string> = {
  new_wig: "new wig consultation",
  in_store: "in-store consultation",
  virtual: "virtual consultation",
  phone: "phone consultation",
  wash_set: "wash & set",
  restyle: "new wig cut / recut",
  color: "color",
  deep_clean: "keratin / deep clean",
  repair: "repair",
  resize: "resize",
  other: "other",
};

export function allowedTypesForDay(
  allowedTypesByDay: unknown,
  dayOfWeek: number
): string[] | null {
  if (!allowedTypesByDay || typeof allowedTypesByDay !== "object") return null;
  const list = (allowedTypesByDay as Record<string, unknown>)[String(dayOfWeek)];
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.map(String);
}

export function typeAllowedOnDay(
  allowedTypesByDay: unknown,
  dayOfWeek: number,
  type: string
): boolean {
  const allowed = allowedTypesForDay(allowedTypesByDay, dayOfWeek);
  return !allowed || allowed.includes(type);
}

function phraseAllowedTypes(allowed: string[]): string {
  const labels = allowed.map((t) => TYPE_LABELS[t] || t.replace(/_/g, " "));
  // Collapse the four consultation channels into one phrase when all
  // present, so the customer message reads naturally.
  const consultTypes = ["new_wig", "in_store", "virtual", "phone"];
  let phrased: string[];
  if (consultTypes.every((t) => allowed.includes(t))) {
    phrased = [
      "new wig consultations",
      ...allowed
        .filter((t) => !consultTypes.includes(t))
        .map((t) => `${TYPE_LABELS[t] || t.replace(/_/g, " ")}s`),
    ];
  } else {
    phrased = labels.map((l) => `${l}s`);
  }
  return phrased.length > 1
    ? `${phrased.slice(0, -1).join(", ")} and ${phrased[phrased.length - 1]}`
    : phrased[0];
}

export function dayRestrictionMessage(
  dayOfWeek: number,
  allowed: string[]
): string {
  return `On ${DAY_NAMES[dayOfWeek]}s we only take ${phraseAllowedTypes(allowed)}. Please pick another day for this service.`;
}

/** Calendar-legend form: "Sundays: new wig consultations and repairs only". */
export function dayAllowanceLabel(
  dayOfWeek: number,
  allowed: string[]
): string {
  return `${DAY_NAMES[dayOfWeek]}s: ${phraseAllowedTypes(allowed)} only`;
}
